export const GUEST_DEMO_MINUTE_FIVE_CHOICE_EXPERIMENT_KEY = "guest_demo_minute_five_choice_v1";

export const GUEST_DEMO_MINUTE_FIVE_CHOICE_VARIANTS = {
  control: "control",
  combinedChoice: "combined_choice",
} as const;

export type GuestDemoMinuteFiveChoiceVariant =
  (typeof GUEST_DEMO_MINUTE_FIVE_CHOICE_VARIANTS)[keyof typeof GUEST_DEMO_MINUTE_FIVE_CHOICE_VARIANTS];

export interface GuestDemoMinuteFiveChoiceAssignment {
  assignmentId: string;
  variant: GuestDemoMinuteFiveChoiceVariant;
}

export const GUEST_DEMO_MINUTE_FIVE_CHOICE_STORAGE_KEY =
  "chickytutor:experiment:guest-demo-minute-five-choice:v1";

const EXPERIMENT_BUCKET_COUNT = 10_000;
const DEFAULT_TREATMENT_PERCENT = 0;

function fnv1a(value: string): number {
  let hash = 0x811c9dc5;

  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }

  return hash >>> 0;
}

function createAssignmentId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }

  return `guest_choice_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

export function getGuestDemoMinuteFiveChoiceBucket(seed: string): number {
  if (!seed.trim()) return EXPERIMENT_BUCKET_COUNT - 1;

  return fnv1a(`${GUEST_DEMO_MINUTE_FIVE_CHOICE_EXPERIMENT_KEY}:${seed}`) % EXPERIMENT_BUCKET_COUNT;
}

export function assignGuestDemoMinuteFiveChoiceVariant({
  seed,
  treatmentPercent = DEFAULT_TREATMENT_PERCENT,
}: {
  seed: string;
  treatmentPercent?: number;
}): GuestDemoMinuteFiveChoiceVariant {
  if (!seed.trim()) return GUEST_DEMO_MINUTE_FIVE_CHOICE_VARIANTS.control;

  const normalizedPercent = Math.min(100, Math.max(0, treatmentPercent));
  const treatmentBucketCount = Math.round(normalizedPercent * 100);

  return getGuestDemoMinuteFiveChoiceBucket(seed) < treatmentBucketCount
    ? GUEST_DEMO_MINUTE_FIVE_CHOICE_VARIANTS.combinedChoice
    : GUEST_DEMO_MINUTE_FIVE_CHOICE_VARIANTS.control;
}

export function getGuestDemoMinuteFiveChoiceTreatmentPercent(
  rawValue = process.env.NEXT_PUBLIC_GUEST_DEMO_MINUTE_FIVE_CHOICE_PERCENT
): number {
  if (!rawValue?.trim()) return DEFAULT_TREATMENT_PERCENT;

  const parsed = Number(rawValue);
  return Number.isFinite(parsed) ? Math.min(100, Math.max(0, parsed)) : DEFAULT_TREATMENT_PERCENT;
}

export function getOrCreateGuestDemoMinuteFiveChoiceAssignment({
  storage = typeof window === "undefined" ? null : window.localStorage,
  treatmentPercent = getGuestDemoMinuteFiveChoiceTreatmentPercent(),
}: {
  storage?: Storage | null;
  treatmentPercent?: number;
} = {}): GuestDemoMinuteFiveChoiceAssignment {
  if (!storage) {
    return {
      assignmentId: "storage_unavailable",
      variant: GUEST_DEMO_MINUTE_FIVE_CHOICE_VARIANTS.control,
    };
  }

  try {
    let assignmentId = storage.getItem(GUEST_DEMO_MINUTE_FIVE_CHOICE_STORAGE_KEY)?.trim();
    if (!assignmentId) {
      assignmentId = createAssignmentId();
      storage.setItem(GUEST_DEMO_MINUTE_FIVE_CHOICE_STORAGE_KEY, assignmentId);
    }

    return {
      assignmentId,
      variant: assignGuestDemoMinuteFiveChoiceVariant({
        seed: assignmentId,
        treatmentPercent,
      }),
    };
  } catch {
    return {
      assignmentId: "storage_unavailable",
      variant: GUEST_DEMO_MINUTE_FIVE_CHOICE_VARIANTS.control,
    };
  }
}
